import React, { useState, useEffect } from 'react';
import './OppgaverGruppert.css';
import crmApi from '../api/crm';

export default function OppgaverGruppert({ sakId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({
    prosess: true,
    epost: true,
    dokument: true,
    utredning: true,
    generelt: true
  });

  useEffect(() => {
    loadOppgaver();
  }, [sakId]);

  const loadOppgaver = async () => {
    try {
      setLoading(true);
      const result = await crmApi.request(`/oppgaver/sak/${sakId}`);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const getGroupIcon = (group) => {
    const icons = {
      prosess: '⚖️',
      epost: '📧',
      dokument: '📄',
      utredning: '🔍',
      generelt: '📝'
    };
    return icons[group] || '📝';
  };

  const getGroupName = (group) => {
    const names = {
      prosess: 'Prosess & Frister',
      epost: 'E-post TODO-er',
      dokument: 'Dokumentoppgaver',
      utredning: 'Utredninger',
      generelt: 'Generelle oppgaver'
    };
    return names[group] || group;
  };

  const getGroupDescription = (group) => {
    const descriptions = {
      prosess: 'Prosessuelle frister, møter og innleveringer',
      epost: 'Oppgaver fra innkommende e-poster',
      dokument: 'Oppgaver generert fra stevninger og kontrakter',
      utredning: 'Juridiske vurderinger og research',
      generelt: 'Øvrige oppgaver'
    };
    return descriptions[group] || '';
  };

  const getPriorityColor = (prioritet) => {
    const colors = {
      høy: '#ef4444',
      middels: '#f59e0b',
      lav: '#6b7280'
    };
    return colors[prioritet] || '#6b7280';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('nb-NO');
  };

  const getDaysUntil = (timestamp) => {
    if (!timestamp) return null;
    const days = Math.ceil((timestamp - Date.now()) / (1000 * 60 * 60 * 24));
    if (days < 0) return `${Math.abs(days)} dager forsinket`;
    if (days === 0) return 'I dag';
    if (days === 1) return 'I morgen';
    return `${days} dager`;
  };

  if (loading) {
    return <div className="oppgaver-loading">Laster oppgaver...</div>;
  }

  if (error) {
    return <div className="oppgaver-error">Feil: {error}</div>;
  }

  if (!data) {
    return <div className="oppgaver-empty">Ingen data tilgjengelig</div>;
  }

  const groups = ['prosess', 'epost', 'dokument', 'utredning', 'generelt'];

  return (
    <div className="oppgaver-gruppert">
      <div className="oppgaver-header">
        <h2>📋 Oppgaver organisert per type</h2>
        <button className="refresh-btn" onClick={loadOppgaver}>🔄 Oppdater</button>
      </div>

      {/* Statistikk */}
      {data.statistikk && (
        <div className="statistikk-grid">
          <div className="stat-card">
            <div className="stat-value">{data.statistikk.totalt}</div>
            <div className="stat-label">Totalt aktive</div>
          </div>
          <div className="stat-card kritisk">
            <div className="stat-value">{data.statistikk.kritiske_frister}</div>
            <div className="stat-label">Kritiske frister</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{data.statistikk.epost_venter}</div>
            <div className="stat-label">E-post venter</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{data.statistikk.forfaller_denne_uken}</div>
            <div className="stat-label">Denne uken</div>
          </div>
        </div>
      )}

      {/* Grupperte oppgaver */}
      <div className="oppgaver-groups">
        {groups.map(group => {
          const oppgaver = data.oppgaver[group] || [];
          if (oppgaver.length === 0) return null;

          return (
            <div key={group} className="oppgave-group">
              <div
                className="group-header"
                onClick={() => toggleGroup(group)}
              >
                <div className="group-title">
                  <span className="group-icon">{getGroupIcon(group)}</span>
                  <div>
                    <h3>{getGroupName(group)} ({oppgaver.length})</h3>
                    <p className="group-description">{getGroupDescription(group)}</p>
                  </div>
                </div>
                <span className="expand-icon">
                  {expandedGroups[group] ? '▼' : '▶'}
                </span>
              </div>

              {expandedGroups[group] && (
                <div className="group-content">
                  {oppgaver.map(oppgave => (
                    <div key={oppgave.id} className="oppgave-item">
                      <div className="oppgave-checkbox">
                        <input
                          type="checkbox"
                          checked={oppgave.status === 'fullført'}
                          readOnly
                        />
                      </div>

                      <div className="oppgave-main">
                        <div className="oppgave-title-row">
                          <h4>{oppgave.tittel}</h4>
                          {oppgave.kritisk_frist && (
                            <span className="badge kritisk">🔴 Kritisk</span>
                          )}
                          {oppgave.prioritet && (
                            <span
                              className="priority-badge"
                              style={{ backgroundColor: getPriorityColor(oppgave.prioritet) }}
                            >
                              {oppgave.prioritet}
                            </span>
                          )}
                        </div>

                        {oppgave.beskrivelse && (
                          <p className="oppgave-beskrivelse">{oppgave.beskrivelse}</p>
                        )}

                        <div className="oppgave-meta">
                          {oppgave.forfall_dato && (
                            <span className="meta-item">
                              📅 {formatDate(oppgave.forfall_dato)}
                              <span className="days-until">
                                ({getDaysUntil(oppgave.forfall_dato)})
                              </span>
                            </span>
                          )}

                          {oppgave.estimert_timer && (
                            <span className="meta-item">
                              ⏱️ {oppgave.estimert_timer}t
                            </span>
                          )}

                          {oppgave.kilde_type && (
                            <span className="meta-item">
                              🔗 Fra: {oppgave.kilde_type}
                            </span>
                          )}
                        </div>

                        {/* Deloppgaver */}
                        {oppgave.deloppgaver && oppgave.deloppgaver.length > 0 && (
                          <div className="deloppgaver">
                            <div className="deloppgaver-header">
                              <span>📋 Deloppgaver ({oppgave.fullførte_deloppgaver}/{oppgave.antall_deloppgaver})</span>
                              <div className="progress-bar">
                                <div
                                  className="progress-fill"
                                  style={{ width: `${oppgave.progresjon_prosent || 0}%` }}
                                />
                              </div>
                              <span>{oppgave.progresjon_prosent || 0}%</span>
                            </div>
                            <ul className="deloppgaver-liste">
                              {oppgave.deloppgaver.map(del => (
                                <li key={del.id}>
                                  <input
                                    type="checkbox"
                                    checked={del.status === 'fullført'}
                                    readOnly
                                  />
                                  <span>{del.tittel}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {data.statistikk.totalt === 0 && (
        <div className="oppgaver-empty">
          <p>✅ Ingen aktive oppgaver for denne saken</p>
        </div>
      )}
    </div>
  );
}
